import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, message, Popconfirm } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../shared/permissions';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import {
    type RemovePermissionOnRoleMutation,
    type RemovePermissionOnRoleMutationVariables,
    RemovePermissionOnRoleDocument,
} from '../../../api/mutations/removePermissionOnRole';
import hasPermissions from '../../../utilities/hasPermissions';

export type RolePermissionListActionsProps = {
    role: RoleSpecsFragment;
    permission: RoleSpecsFragment['permissions'][number];
};

const RolePermissionListActions = ({ permission, role }: RolePermissionListActionsProps) => {
    const { t } = useTranslation(['roleDetails']);

    const apolloClient = useApolloClient();
    const permissionId = permission.id;
    const roleId = role.id;

    const onDelete = useCallback(async () => {
        message.loading({
            content: t('roleDetails:deletePermission.messages.submitting'),
            key: 'primary',
            duration: 0,
        });

        await apolloClient
            .mutate<RemovePermissionOnRoleMutation, RemovePermissionOnRoleMutationVariables>({
                mutation: RemovePermissionOnRoleDocument,
                variables: { roleId, permissionId },
            })
            .finally(() => {
                message.destroy('primary');
            });

        message.success({
            content: t('roleDetails:deletePermission.messages.success'),
            key: 'primary',
        });
    }, [t, apolloClient, permissionId, roleId]);

    if (!hasPermissions(role.rolePermissions, [permissionKind.updateRole])) {
        return null;
    }

    return (
        <Popconfirm
            cancelText={t('roleDetails:deletePermission.cancelText')}
            icon={<WarningOutlined style={{ color: 'red' }} />}
            okText={t('roleDetails:deletePermission.okText')}
            onConfirm={onDelete}
            placement="left"
            title={t('roleDetails:deletePermission.message')}
        >
            <Button icon={<DeleteOutlined />} type="link" />
        </Popconfirm>
    );
};

export default RolePermissionListActions;
